/**
 * @file integrationSwitches.ts
 * @brief Super Admin → Integrations (W32, BACKEND B42): parsing and words for
 *        the platform and per-company Zapier / Make switches.
 *
 * @details
 * Platform: live = server env (`<APP>_ENABLED`, only a deploy changes it) AND
 * the Super Admin switch. Off on the server = the switch is read-only. Paused
 * = env on, switch off: OAuth and the API refuse, hooks wait and are sent
 * when it is back on (nothing is lost, nobody has to reconnect).
 * Company: the same stored `zapier` / `make` opt-in as Company → Features.
 *
 * Pure (no React, no "@/" imports) so node:test can load it.
 *
 * @references
 * - scratch/claude-code-report.md "## B42" (contract)
 * - BACKEND src/features/zapier/automationPlatform.mjs
 *
 * @author DES (prepared with Claude)
 * @date 30-10-2026
 */

export const INTEGRATION_APPS = ["zapier", "make"] as const;
export type IntegrationApp = (typeof INTEGRATION_APPS)[number];

export const APP_NAME: Record<IntegrationApp, string> = { zapier: "Zapier", make: "Make" };

/** What a workflow is called inside each app. */
const FLOWS: Record<IntegrationApp, string> = { zapier: "Zaps", make: "scenarios" };

export interface PlatformApp {
  name: string;
  serverEnabled: boolean;
  platformEnabled: boolean;
  live: boolean;
  updatedAt: string | null;
  connections: number;
  hooks: number;
  pendingDeliveries: number;
}

export type PlatformApps = Record<IntegrationApp, PlatformApp>;

export interface CompanyAppState {
  enabled: boolean;
  connections: number;
  hooks: number;
}

export interface IntegrationCompany {
  id: string;
  name: string;
  status: string;
  zapier: CompanyAppState;
  make: CompanyAppState;
}

export interface IntegrationCompaniesPage {
  page: number;
  limit: number;
  total: number;
  companies: IntegrationCompany[];
}

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.max(0, v) : 0);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});

function parsePlatformApp(app: IntegrationApp, raw: unknown): PlatformApp {
  const r = obj(raw);
  const serverEnabled = r.serverEnabled === true;
  // A missing switch is ON (B42: no row = on).
  const platformEnabled = r.platformEnabled !== false;
  return {
    name: typeof r.name === "string" && r.name ? r.name : APP_NAME[app],
    serverEnabled,
    platformEnabled,
    live: typeof r.live === "boolean" ? r.live : serverEnabled && platformEnabled,
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : null,
    connections: num(r.connections),
    hooks: num(r.hooks),
    pendingDeliveries: num(r.pendingDeliveries),
  };
}

/** `{ success, apps: { zapier, make } }` → both apps, missing fields filled. */
export function parsePlatform(raw: unknown): PlatformApps {
  const apps = obj(obj(raw).apps);
  return { zapier: parsePlatformApp("zapier", apps.zapier), make: parsePlatformApp("make", apps.make) };
}

function parseCompanyApp(raw: unknown): CompanyAppState {
  const r = obj(raw);
  return { enabled: r.enabled === true, connections: num(r.connections), hooks: num(r.hooks) };
}

export function parseCompany(raw: unknown): IntegrationCompany {
  const r = obj(raw);
  return {
    id: String(r.id ?? ""),
    name: typeof r.name === "string" && r.name ? r.name : "Unnamed company",
    status: typeof r.status === "string" ? r.status : "",
    zapier: parseCompanyApp(r.zapier),
    make: parseCompanyApp(r.make),
  };
}

export function parseCompaniesPage(raw: unknown): IntegrationCompaniesPage {
  const r = obj(raw);
  const list = Array.isArray(r.companies) ? r.companies : [];
  return {
    page: num(r.page) || 1,
    limit: num(r.limit) || 25,
    total: num(r.total),
    companies: list.map(parseCompany).filter((c) => c.id),
  };
}

export type AppStatus = { label: string; tone: "success" | "warning" | "muted" };

/** The chip on a platform card. */
export function platformStatus(a: PlatformApp): AppStatus {
  if (!a.serverEnabled) return { label: "Off on the server", tone: "muted" };
  if (!a.platformEnabled) return { label: "Paused", tone: "warning" };
  return { label: "Live", tone: "success" };
}

/** The read-only line under a card whose env master switch is off. */
export function serverOffLine(app: IntegrationApp): string {
  return `Off on the server (${app.toUpperCase()}_ENABLED). Only a deploy turns it on; this switch has no effect until then.`;
}

export type ConfirmCopy = { title: string; body: string; action: string };

/** The confirm dialog for a platform switch. */
export function platformConfirm(app: IntegrationApp, next: boolean, a?: Pick<PlatformApp, "connections" | "pendingDeliveries">): ConfirmCopy {
  const name = APP_NAME[app];
  if (!next) {
    return {
      title: `Turn off ${name}?`,
      body:
        `Turning off pauses all ${name} connections for every company. ` +
        `Nobody can connect ${name}, and its ${FLOWS[app]} get "not available" until you turn it back on. ` +
        `Connections are kept and events wait, so nothing is lost and nobody has to reconnect.`,
      action: `Turn off ${name}`,
    };
  }
  const waiting = a?.pendingDeliveries ?? 0;
  return {
    title: `Turn ${name} back on?`,
    body:
      `Every company that allows ${name} can use it again.` +
      (waiting > 0 ? ` ${waiting} waiting ${waiting === 1 ? "event is" : "events are"} sent within a minute.` : ""),
    action: `Turn on ${name}`,
  };
}

/** The confirm dialog for one company's switch. */
export function companyConfirm(app: IntegrationApp, next: boolean, company: Pick<IntegrationCompany, "name">): ConfirmCopy {
  const name = APP_NAME[app];
  return next
    ? {
        title: `Allow ${name} for ${company.name}?`,
        body: `Everyone at ${company.name} can connect ${name}. Their company admin sees this switch on in Company → Features.`,
        action: `Allow ${name}`,
      }
    : {
        title: `Turn off ${name} for ${company.name}?`,
        body: `Nobody at ${company.name} can connect or use ${name} until it is turned back on, here or by their company admin in Company → Features.`,
        action: `Turn off ${name}`,
      };
}

/** "1 connection · 2 Zaps" for a company cell. */
export function companyUsage(app: IntegrationApp, s: CompanyAppState): string {
  const c = `${s.connections} ${s.connections === 1 ? "connection" : "connections"}`;
  if (s.hooks === 0) return c;
  const flow = FLOWS[app];
  return `${c} · ${s.hooks} active ${s.hooks === 1 ? flow.slice(0, -1) : flow}`;
}

export function totalPages(p: Pick<IntegrationCompaniesPage, "total" | "limit">): number {
  return Math.max(1, Math.ceil(p.total / Math.max(1, p.limit)));
}
